// ============================================================================
// Tender Domain Types — Parallel to procurement types (AP flow)
// ============================================================================

export interface TenderActivity {
  id: number
  tender_ref: string
  title: string
  description: string | null
  category: 'works' | 'services' | 'goods'
  authority: string | null
  estimated_value: number | null
  currency: string
  business_unit: string | null
  cpv_code?: string | null                 // Common Procurement Vocabulary code (data spine)
  evaluation_model?: EvaluationModel       // Phase 5 — policy-seeded weighting/scale/criteria/moderation
  current_stage: number                    // 0-9
  stage_history: StageHistoryEntry[]
  gate_states: Record<string, GateState>
  active_operator: string | null           // O1-O10
  scenario_id: string | null
  expected_publish_date: string | null
  actual_publish_date: string | null
  submission_deadline: string | null
  award_date: string | null
  created_at: string | null
  created_by?: string | null              // tender owner (set by backend)
  // Nested relations (loaded by GET /api/tenders/{ref})
  route?: TenderRoute
  business_case?: TenderBusinessCase
  bids?: TenderBid[]
  policy_checks?: TenderPolicyCheck[]
  notices?: TenderNotice[]
}

export interface StageHistoryEntry {
  stage: number
  entered_at: string
  exited_at: string | null
  operator: string
}

export interface GateState {
  status: 'not_reached' | 'pending' | 'approved' | 'returned'
  approver: string | null
  decided_at: string | null
  rationale: string | null
}

export interface TenderRoute {
  id: number
  route_type: 'open_competition' | 'framework_calloff' | 'direct_award'
  framework_ref: string | null
  rationale: string | null
  value_threshold_check: boolean | null
  validation_checklist: ValidationCheck[]
  /** The deterministic AI-recommended route (may differ from route_type on manual override). */
  recommended_route_type?: 'open_competition' | 'framework_calloff' | 'direct_award' | null
  gate1_approved_by: string | null
  gate1_approved_at: string | null
}

export interface ValidationCheck {
  check: string
  result: boolean
  notes: string
}

export interface TenderBusinessCase {
  id: number
  strategic_case: Record<string, unknown> | null
  economic_case: Record<string, unknown> | null
  commercial_case: Record<string, unknown> | null
  financial_case: Record<string, unknown> | null
  management_case: Record<string, unknown> | null
  citations: Citation[]
  generation_model: string | null
  version: number
}

export interface Citation {
  doc_id: string
  doc_title: string
  clause_ref: string | null
  text_snippet: string
  chunk_id?: number
  page_number?: number
}

export type BidStatus =
  | 'invited' | 'declined_to_bid' | 'no_response' | 'submitted'
  | 'shortlisted' | 'preferred_bidder' | 'awarded' | 'unsuccessful'
  | 'evaluated' | 'excluded'

export interface TenderBid {
  id: number
  tender_id: number
  bidder_name: string
  bidder_ref: string | null
  bidder_legal_entity?: string | null
  submission_format: string | null
  submitted_at?: string | null
  is_sme?: boolean
  is_vcse?: boolean
  status?: BidStatus
  references_status?: string | null
  total_price: number | null
  annual_cost: number | null
  currency: string
  quality_score: number | null
  price_score: number | null
  social_value_score: number | null
  overall_score: number | null
  quality_answers: QualityAnswer[]
  mandatory_checklist: MandatoryCheck[]
  flags: PolicyFlag[]
  is_excluded: boolean
  exclusion_reason: string | null
  exclusion_policy_id: string | null
  is_mat: boolean
  mat_rationale: string | null
  evaluated_at?: string | null
}

export interface QualityAnswer {
  criterion_id: string
  question: string
  response_text: string
  score: number
  max_score: number
  // Phase 5 — weighted 0-N matrix + consensus moderation
  weight?: number
  raw?: number | null
  consensus?: number | null
  panel_scores?: (number | { assessor: string; score: number })[]
  scoring_rationale?: string
}

export interface EvaluationModel {
  weighting?: { quality?: number; price?: number; social_value?: number }
  scoring_scale?: string                 // "0-3" | "0-5"
  price_method?: string
  quality_criteria?: { id: string; topic: string; weight: number }[]
  moderation?: {
    consensus?: boolean
    panel?: string[]
    conflicts?: { member: string; related_bidder: string; detail?: string; cleared?: boolean }[]
  }
  mandatory_gates?: string[]
}

export interface MandatoryCheck {
  requirement: string
  pass: boolean
  notes: string
}

export interface PolicyFlag {
  policy_id: string
  flag_type: string
  rationale: string
  severity: 'pass' | 'flag' | 'block'
  threshold_data?: Record<string, unknown>
}

export interface TenderPolicyCheck {
  id: number
  policy_id: string
  policy_name: string | null
  policy_version: string
  source_clause: string | null
  result: 'PASS' | 'FLAG' | 'BLOCK'
  rationale: string | null
  bid_id: number | null
  gate_id?: string | null
  stage: number | null
  operator_id: string | null
  checked_at: string | null
}

export interface TenderNotice {
  id: number
  notice_type: string
  title: string
  published: boolean
  structured_data?: NoticeStructuredData
}

export interface NoticeStructuredData {
  contracting_authority?: string
  procurement_reference?: string
  cpv_code?: string | null
  procurement_procedure?: string
  estimated_value?: number | null
  currency?: string
  category?: string
  sme_suitable?: boolean
  vcse_suitable?: boolean
  closing_date?: string | null
  closing_time?: string | null
  submission_deadline?: string | null
  attachments?: { name: string; tag: string }[]
  [key: string]: unknown
}

export interface TenderClarification {
  id: number
  clarification_ref: string | null
  question: string
  asked_by: string | null
  asked_at: string | null
  draft_answer: string | null
  status: 'pending' | 'drafted' | 'approved' | 'issued'
  is_shared_to_all: boolean
  answered_at: string | null
  issued_at: string | null
}

export interface TenderKPIRecord {
  id: number
  kpi_name: string
  kpi_period: string | null
  target: number
  actual: number
  unit: string | null
  is_breached: boolean
  breach_severity: string | null
  service_credit_amount: number
  service_credit_formula: string | null
  s71_triggered: boolean
}

// ============================================================================
// Constants
// ============================================================================

export const TENDER_STAGES = [
  // BRD v1.2: O1 is a CROSS-CUTTING layer, always active. It appears as an overlay on all stages.
  // Do NOT gate O1 to Stage 0 only. The pipeline visibility indicator is shown at every stage.
  { stage: 0, label: 'Pipeline', shortLabel: 'Pipeline', icon: 'BarChart3', operators: ['O1'], gate: null,
    note: 'O1 is cross-cutting — also shown as persistent overlay at all other stages (BRD v1.2)' },
  { stage: 1, label: 'Demand & Business Case', shortLabel: 'Demand', icon: 'FileText', operators: ['O2', 'O3'], gate: 'G1' },
  { stage: 2, label: 'Strategy & Route', shortLabel: 'Route', icon: 'GitBranch', operators: ['O3'], gate: 'G1' },
  { stage: 3, label: 'Market Engagement', shortLabel: 'Market', icon: 'Globe', operators: ['O4'], gate: null },
  { stage: 4, label: 'Execution (ITT)', shortLabel: 'ITT', icon: 'Send', operators: ['O4', 'O5'], gate: 'G2' },
  { stage: 5, label: 'Evaluation & Award', shortLabel: 'Evaluate', icon: 'Scale', operators: ['O6', 'O7'], gate: 'G3' },
  { stage: 6, label: 'Contracting', shortLabel: 'Contract', icon: 'FileSignature', operators: ['O8'], gate: 'G4' },
  { stage: 7, label: 'Supplier Onboarding', shortLabel: 'Onboard', icon: 'UserPlus', operators: [], gate: null },
  { stage: 8, label: 'Contract Management', shortLabel: 'Manage', icon: 'Activity', operators: ['O9', 'O10'], gate: 'G5' },
  { stage: 9, label: 'Renewal / Exit', shortLabel: 'Renew', icon: 'RefreshCw', operators: [], gate: null },
] as const

export const GATE_CONFIG: Record<string, { label: string; role: string; description: string }> = {
  // BRD v1.2 gate descriptions:
  G1: { label: 'Gate 1 — SRO Approval', role: 'Senior Responsible Officer', description: 'Confirm the right suppliers and procurement route before market publication' },
  G2: { label: 'Gate 2 — Officer Review', role: 'Procurement Officer', description: 'Review and approve AI-drafted clarification responses before distribution' },
  // BRD v1.2: G3 is Bid Response Closure — confirms bid window is closed BEFORE evaluation begins
  G3: { label: 'Gate 3 — Bid Response Closure', role: 'Moderator / SRO', description: 'Confirm lock-down of inbound bids before evaluation begins (bid window closure)' },
  G4: { label: 'Gate 4 — Governance', role: 'Governance Panel', description: 'Mandatory review of the complete contract documentation set prior to award and signature' },
  G5: { label: 'Gate 5 — Post-Award Exception', role: 'SRO', description: 'Review KPI breaches and service credit adjustments (post-award exception escalation)' },
}

export const POLICY_DISPLAY: Record<string, { label: string; source: string; color: string }> = {
  'PP-01': { label: 'Transparency', source: 'PA23 s.19', color: 'text-blue-700 bg-blue-50' },
  'PP-02': { label: 'Equal Treatment', source: 'PA23 s.12', color: 'text-indigo-700 bg-indigo-50' },
  'PP-03': { label: 'Abnormal Low Bid', source: 'Sourcing Playbook §4.3', color: 'text-red-700 bg-red-50' },
  'PP-04': { label: 'Mandatory Pass/Fail', source: 'ITT Spec §2', color: 'text-orange-700 bg-orange-50' },
  'PP-05': { label: 'Social Value', source: 'NPPS §3.1', color: 'text-green-700 bg-green-50' },
  'PP-06': { label: 'Conflict of Interest', source: 'GovS 008 §3.7', color: 'text-amber-700 bg-amber-50' },
  'PP-07': { label: 'MAT Selection', source: 'PA23 s.19', color: 'text-purple-700 bg-purple-50' },
  'PP-08': { label: 'Award Transparency', source: 'PA23 s.53', color: 'text-teal-700 bg-teal-50' },
}

export const formatGBP = (amount: number) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(amount)

// Multi-currency formatter — PRIMARY formatter for all tender monetary values
// currency comes from TenderActivity.currency (set from TENDER_DEFAULT_CURRENCY env or frontend toggle)
// Do NOT use formatGBP directly in components — always prefer formatCurrency
export const formatCurrency = (amount: number, currency: string = 'GBP') =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount)

// TenderCurrencyContext — wrap app in this provider to propagate currency changes from toggle
// export const TenderCurrencyContext = React.createContext<string>('GBP')  // implemented in Phase 6

// ============================================================================
// Phase 0 — Document Drafting Data Model Interfaces
// ============================================================================

export type FieldProvenance = 'ai_derived' | 'seeded_for_demo' | 'human_required'

export interface AwardReportData {
  id?: number
  tender_id?: number
  // Key details (PA23 Section 2.1)
  delivery_manager?: string | null
  commercial_lead?: string | null
  department?: string | null
  programme_team?: string | null
  sro_name?: string | null
  pro_name?: string | null
  sourcing_strategy_approval_date?: string | null
  budget_holder_name?: string | null
  budget_holder_department?: string | null
  budget_approval_date?: string | null
  // Contract details (PA23 Section 2.2)
  contract_duration_months?: number | null
  initial_value_ex_vat?: number | null
  initial_value_inc_vat?: number | null
  extension_options_months?: string | null
  extension_value_ex_vat?: number | null
  extension_value_inc_vat?: number | null
  fy_breakdown?: { fy: string; amount: number }[] | null
  contract_expiry_date?: string | null
  contract_classification?: 'Bronze' | 'Silver' | 'Gold' | null
  spend_controls_obtained?: Record<string, unknown> | null
  conditions_of_approval?: string | null
  conditions_met?: string | null
  // Narratives
  background_narrative?: string | null
  conflicts_narrative?: string | null
  market_engagement_narrative?: string | null
  procurement_process_narrative?: string | null
  modification_details?: string | null
  award_recommendation_narrative?: string | null
  benefits_savings_narrative?: string | null
  // Structured tables
  key_personnel?: { name: string; org: string; role: string }[] | null
  assessment_panel?: { name: string; title: string; org: string; role: string }[] | null
  key_risks?: { risk: string; mitigation: string }[] | null
  policy_risks?: Record<string, string> | null
  technical_commercial_split?: { technical: number; commercial: number } | null
  // Spend controls
  cabinet_office_controls?: Record<string, unknown> | null
  hmt_approvals?: Record<string, unknown> | null
  // Approvals
  signature_blocks?: { role: string; name: string | null; position: string; signed: boolean; date: string | null }[] | null
  version_control?: { version: string; author: string; date: string; changes: string }[] | null
  // Provenance
  field_provenance?: Record<string, FieldProvenance> | null
  created_at?: string | null
  updated_at?: string | null
}

export interface ContractFormData {
  id?: number
  tender_id?: number
  // Order Form fields (28 rows)
  buyer_cause_option?: 'option_a' | 'option_b' | null
  collaborative_working?: boolean | null
  financial_transparency?: boolean | null
  extension_period_details?: string | null
  extension_notice_period?: string | null
  termination_notice_period?: string | null
  special_terms?: { number: number; text: string }[] | null
  environmental_policy?: string | null
  security_requirements_option?: string | null
  goods_rejection_period?: string | null
  charges_details?: string | null
  indexation_applicable?: boolean | null
  estimated_year1_charges?: number | null
  reimbursable_expenses?: string | null
  payment_method?: string | null
  kpi_details?: {
    service_credit_cap?: string
    period?: string
    critical_kpi_def?: string
    kpis?: { name: string; target: string; measure: string; credit: string }[]
  } | null
  liability_cap_amount?: number | null
  liability_cap_percentage?: number | null
  data_protection_liability_cap?: number | null
  progress_meeting_frequency?: string | null
  report_frequency?: string | null
  guarantor_details?: { name: string; details: string } | null
  virtual_library?: { applicable: boolean; creation_period: string | null; update_period: string | null } | null
  // Contacts (2 contact blocks)
  supplier_contacts?: { role: string; name: string; title: string; email: string; phone: string }[] | null
  buyer_authorized_rep?: { name: string; title: string; email: string; phone: string } | null
  key_subcontractors?: { name: string; registration: string; role: string }[] | null
  // Annexes
  data_processing_details?: Record<string, unknown> | null
  security_core_requirements?: Record<string, boolean> | null
  security_additional_requirements?: Record<string, boolean> | null
  ipr_option?: 'not_applicable' | 'annex_5_part_a' | 'annex_5_part_b' | null
  // Cover letter
  cover_letter_data?: {
    supplier_name?: string
    supplier_address?: string
    contact_name?: string
    contact_email?: string
    buyer_ref?: string
    date?: string
    deliverables_desc?: string
  } | null
  // Signatures
  signature_blocks?: { side: 'buyer' | 'supplier'; name: string | null; role: string; date: string | null }[] | null
  // Provenance
  field_provenance?: Record<string, FieldProvenance> | null
  created_at?: string | null
  updated_at?: string | null
}

export interface BusinessCaseData {
  id?: number
  tender_id?: number
  // Front matter
  organisation_name?: string | null
  project_name?: string | null
  document_version?: string | null
  document_author?: string | null
  further_copies_from?: string | null
  // Project details
  project_sponsor?: string | null
  project_manager?: string | null
  service_description?: string | null
  partner_organisations?: string | null
  project_reference?: string | null
  // Financial tables
  capital_revenue_requirements?: { description: string; value: number; start_date: string | null; end_date: string | null }[] | null
  funding_by_year?: { what_for: string; yr1: number; yr2: number; yr3: number; yr4: number }[] | null
  funding_secured_by_quarter?: Record<string, unknown>[] | null
  staff_resources_by_quarter?: Record<string, unknown>[] | null
  funding_balance?: Record<string, unknown>[] | null
  ie_forecast_5_year?: Record<string, unknown> | null
  financial_benefits?: { description: string; yr1: number; yr2: number; yr3: number; yr4: number; cost_centre: string; budget_holder: string; agreed: boolean }[] | null
  non_financial_benefits?: string | null
  // Management case
  contingency_arrangements?: string | null
  exit_strategy?: string | null
  // Variant
  variant?: 'project' | 'programme' | null
  // Provenance
  field_provenance?: Record<string, FieldProvenance> | null
  created_at?: string | null
  updated_at?: string | null
}

export interface EvaluationReportData {
  id?: number
  tender_id?: number
  evaluation_panel_members?: { name: string; role: string; organisation: string }[] | null
  price_evaluation_methodology?: string | null
  /** Model answers only — weights come from TenderActivity.evaluation_model */
  quality_questions_detail?: { number: number; question: string; model_answer: string }[] | null
  scoring_scale_description?: string | null
  vfm_analysis?: {
    cashable?: { amount: number; description: string }
    non_cashable?: { benefit: string }[]
  } | null
  recommendation_text?: string | null
  approval_signatory?: { name: string; title: string; date: string | null; signed: boolean } | null
  // Provenance
  field_provenance?: Record<string, FieldProvenance> | null
  created_at?: string | null
  updated_at?: string | null
}

export interface AutoPopulateResult {
  status: string
  tender_ref: string
  populated_at: string
  models: {
    award_report: { auto_filled: string[]; human_required: string[]; total_fields: number }
    contract_form: { auto_filled: string[]; human_required: string[]; total_fields: number }
    business_case: { auto_filled: string[]; human_required: string[]; total_fields: number }
    evaluation_report: { auto_filled: string[]; human_required: string[]; total_fields: number }
  }
}
